/** Helpers for dual-interval subscription plans (monthly + yearly). */

export function planMonthlyPrice(plan) {
  if (!plan) return 0;
  const direct = parseFloat(plan.price_monthly);
  if (!Number.isNaN(direct) && direct > 0) return direct;
  const legacy = parseFloat(plan.price);
  if (!Number.isNaN(legacy) && legacy > 0 && plan.billing_interval !== 'yearly') return legacy;
  return 0;
}

export function planYearlyPrice(plan) {
  if (!plan) return 0;
  const direct = parseFloat(plan.price_yearly);
  if (!Number.isNaN(direct) && direct > 0) return direct;
  const legacy = parseFloat(plan.price);
  if (!Number.isNaN(legacy) && legacy > 0 && plan.billing_interval === 'yearly') return legacy;
  return 0;
}

export function planMaxTierPrice(plan) {
  return Math.max(planMonthlyPrice(plan), planYearlyPrice(plan));
}

export function planIsFree(plan) {
  if (!plan) return true;
  if (String(plan.slug || '').toLowerCase() === 'free') return true;
  return planMaxTierPrice(plan) <= 0;
}

export function planOffersBothIntervals(plan) {
  return planMonthlyPrice(plan) > 0 && planYearlyPrice(plan) > 0;
}

export function normalizeBillingChoice(plan, choice) {
  const monthlyOk = planMonthlyPrice(plan) > 0;
  const yearlyOk = planYearlyPrice(plan) > 0;
  if (monthlyOk && yearlyOk) return choice === 'yearly' ? 'yearly' : 'monthly';
  if (yearlyOk) return 'yearly';
  return 'monthly';
}

export function priceForBillingChoice(plan, choice) {
  const interval = normalizeBillingChoice(plan, choice);
  return interval === 'yearly' ? planYearlyPrice(plan) : planMonthlyPrice(plan);
}

/** Free / starter plans sort first; others by sort_order. */
export function sortPlansForDisplay(plans) {
  if (!Array.isArray(plans)) return [];
  return [...plans].sort((a, b) => {
    const af = planIsFree(a) ? 0 : 1;
    const bf = planIsFree(b) ? 0 : 1;
    if (af !== bf) return af - bf;
    return Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0);
  });
}

/** Prefer slug `premium`; otherwise highest sort_order among paid plans. */
export function resolvePopularPaidPlanSlug(plans) {
  const paid = (plans || []).filter((p) => !planIsFree(p));
  if (!paid.length) return null;
  const tagged = paid.find((p) => p.slug === 'premium');
  if (tagged) return tagged.slug;
  return paid.reduce((best, p) =>
    Number(p.sort_order ?? 0) >= Number(best.sort_order ?? 0) ? p : best
  ).slug;
}

export function planTierLabel(plan, popularPaidSlug) {
  if (!plan) return '';
  if (planIsFree(plan)) return 'Starter';
  if (plan.slug === popularPaidSlug) return 'Best value';
  const slug = String(plan.slug || '').toLowerCase();
  if (slug === 'basic') return 'Essentials';
  if (slug === 'professional') return 'Growth';
  if (slug === 'premium') return 'Full suite';
  if (slug === 'enterprise') return 'Scale';
  return 'Pro';
}

export function planTierSubtitle(plan) {
  if (!plan) return '';
  if (planIsFree(plan)) {
    return 'Try the platform — upgrade anytime for premium tools and higher limits.';
  }
  const slug = String(plan.slug || '').toLowerCase();
  if (slug === 'basic') return 'Core tools for solo operators getting their first bookings.';
  if (slug === 'professional') return 'Branding, analytics, and workflows for growing studios.';
  if (slug === 'premium') return 'Everything unlocked — ideal when you are scaling volume.';
  if (slug === 'enterprise') return 'Maximum limits and capabilities for established teams.';
  return plan.description || '';
}
